import { Component, ViewChild } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { AlgoliaHit } from 'instantsearch.js';
import moment from 'moment';
import { AutoMap, exec } from '../../utils/utils';
import { CurbUser, Listing } from '../app.models';
import { OpenHouseScheduler } from '../components/open-house-scheduler/open-house-scheduler';
import { AuthService } from '../services/auth.service';
import { CommonProvider } from '../services/common';
import { ConnectionsService } from '../services/connections.service';
import { OpenHouseSchedingService } from '../services/open-house-scheduling.service';
import { WithId, OpenHouseEventStatus, EventApiTyped } from '../types';

@Component({
    selector: 'scheduling',
    templateUrl: 'scheduling.html',
    styleUrls: ['scheduling.scss'],
    standalone: false
})
export class SchedulingPage {
  listing: Listing;

  @ViewChild('calendar') calendar: OpenHouseScheduler;

  // One or more agents assigned to this listing
  assignedAgents: WithId<CurbUser>[] = [];

  agentsWithScheduledEvents: { agent: AlgoliaHit; events: EventApiTyped[] }[] = [];

  constructor(
    private route: ActivatedRoute,
    private router: Router,
    private auth: AuthService,
    private openHouseSchedulingService: OpenHouseSchedingService,
    private connectionsService: ConnectionsService,
    private common: CommonProvider
  ) {}

  // If the screen resizes the calendar will be broken so on every page enter we re-render it to make sure it's displayed correctly
  ionViewWillEnter() {
    this.calendar?.calendarApi?.render();
  }

  async ionViewDidEnter() {
    const id = this.route.snapshot.paramMap.get('listingId');
    this.listing = await this.auth.getListingById(id);

    if (this.listing.sampleListing) {
      this.router.navigate(['/my-paired-listings']);
      return;
    }

    const [assignedAgents, getEventsResults] = await this.common.withLoading(
      () => Promise.all([
        this.getListingAgents(this.listing), 
        this.openHouseSchedulingService.getAllEventsFromListing(id)
      ]), 
      { loadingText: 'Loading events' }
    );

    if (!getEventsResults.ok) {
      // The `auth.logError` was alreay called in the service
      console.error('Failed to fech open house events');
      await this.common.showDisabledScheduleOnInactiveListings();
      this.router.navigate(['/my-paired-listings']);
      return;
    }

    this.assignedAgents = assignedAgents;
    this.calendar.loadEvents(getEventsResults.data);
  }

  async getListingAgents(listing: Listing) {
    const ids = listing.pairedListingAgents.map((x) => x.id);

    const [results, error] = await exec(Promise.all(ids.map((id) => this.connectionsService.public_profile(id))));

    if (error) {
      console.error('Failed to fetch listing agents for scheduler', error);
      this.auth.logErrors('Failed to fetch listing agents for scheduler: ' + JSON.stringify(error), true);
      return [];
    }

    return results;
  }

  private computeListOfAgentsWithEvents(events: EventApiTyped[]) {
    const agentsWithEvents = new AutoMap<string, EventApiTyped[]>(() => []);

    const sortedEvents = events.sort((a, b) => a.start.getTime() - b.start.getTime());
    const now = moment();

    for (const event of sortedEvents) {
      if (moment(now).isAfter(event.end)) {
        continue;
      }

      if (event.extendedProps.status !== OpenHouseEventStatus.assigned) {
        continue;
      }

      const agent = event.extendedProps.agent;

      if (!agent) {
        this.auth.logErrors(`Agent was missing on an assgined event ${event.extendedProps.eventId}`)
        continue;
      }

      agentsWithEvents.get(agent.objectID).push(event);
    }

    const list: any[] = [];
    for (const events of agentsWithEvents.values()) {
      list.push({
        agent: events[0].extendedProps.agent,
        events,
      });
    }

    this.agentsWithScheduledEvents = list;
  }

  eventsChanged(events: EventApiTyped[]) {
    this.computeListOfAgentsWithEvents(events);
  }

  // UI Handlers bellow

  addNewEmptyEvent() {
    this.calendar.handleDateClick();
  }

  openMyProfile() {
    this.router.navigate(['/my-profile']);
  }

  async openSchedulingInfo() {
    const help = await this.common.alertCtrl.create({
      header: 'Open House Scheduler',
      message:
        'The Open House Scheduler helps agents on teams host Open Houses for each other. Add Open Houses to the schedule below. Unassigned Open Houses can be selected by an agent paired to your team account.',
      buttons: [
        {
          text: 'Ok',
        },
        {
          text: 'Help Section',
          role: 'help',
          handler: () => this.common.openLink('https://juuj.me/team-admin-scheduler'),
        },
      ],
    });

    await help.present();
  }
}
